import type { ToastOptions } from '@familyjs/core/components';
import { toastController } from '@familyjs/core/components';
import { defineCustomElement } from '@familyjs/core/components/fml-toast.js';
import { useCallback } from 'react';

import type { HookOverlayOptions } from './HookOverlayOptions';
import { useController } from './useController';

/**
 * A hook for presenting/dismissing an FmlToast component
 * @returns Returns the present and dismiss methods in an array
 */
export function useFmlToast(): UseFmlToastResult {
  const controller = useController<ToastOptions, HTMLFmlToastElement>('FmlToast', toastController, defineCustomElement);

  const present = useCallback(
    (messageOrOptions: string | (ToastOptions & HookOverlayOptions), duration?: number) => {
      if (typeof messageOrOptions === 'string') {
        return controller.present({
          message: messageOrOptions,
          duration,
        });
      } else {
        return controller.present(messageOrOptions);
      }
    },
    [controller.present]
  );

  return [present, controller.dismiss];
}

export type UseFmlToastResult = [
  {
    /**
     * Presents the toast
     * @param message Message to be shown in the toast.
     * @param duration Optional - How many milliseconds to wait before hiding the toast. By default, it will show until dismissToast() is called.
     */
    (message: string, duration?: number): Promise<void>;
    /**
     * Presents the Toast
     * @param options The options to pass to the FmlToast.
     */
    (options: ToastOptions & HookOverlayOptions): Promise<void>;
  },
  /**
   * Dismisses the toast
   */
  () => Promise<void>
];
